import tenantData from '@data';
import tenantUtils from '@utils';
import tenantConstants from '@constants';
import { t } from 'i18next';
import tenantTheme from '@theme';
import store from '@store';
import { capitalizeFirstLetter } from '../../../utility/utility';

export const postedToBayut = (e) => {
  switch (e) {
    case 'not_listed':
      return false;
    case undefined:
      return false;
    case 'limit':
      return false;
    default:
      return true;
  }
};

const getSummaryData = (data, statuses) => {
  const arr = Object.keys(data)?.length > 0 ? Object.keys(data) : [];
  const stats = {};
  arr.forEach((e) => {
    const status = statuses?.find((it) => it.slug == e);
    stats[capitalizeFirstLetter(e)] = {
      title: status?.name,
      mapping: status?.id,
      total: data[e],
    };
  });
  return stats;
};

export const platformMapper = (e, user, platform, setStatsLoading = true, existingStats) => {
  let platformListingId;
  if(!user?.isMultiPlatform) {
    const possiblePlatforms = ['bayut', 'dubizzle'] // replace it with Object.keys in future and check if platform_listing_id is present in any key, store it.
    possiblePlatforms?.forEach(gccPlatform => {
      if(e?.platforms?.[gccPlatform]) {
        platformListingId = e?.platforms?.[gccPlatform]?.platform_listing_id
      }
    })
  }

  return {
    id: e.id,
    platform_listing_id: platformListingId, // e?.platforms?.[platform?.slug]?.platform_listing_id,
    property_id: e?.id,
    slug: platform?.slug,
    ...(setStatsLoading && { stats: { views: 'loading', clicks: 'loading', leads: 'loading' } }),
    ...(!setStatsLoading && existingStats && { stats: existingStats?.platforms?.[platform?.slug]?.stats }),
    showPostedAt: e?.platforms?.[platform?.slug]?.status?.slug === 'active',
    postedOn: e?.platforms?.[platform?.slug]?.posted_at,
    status: tenantUtils.listingStatusMapper(e?.status?.slug),
    public_url: tenantUtils.getLocalisedString(e?.platforms?.[platform?.slug], 'url'),
    disposition: tenantUtils.listingDispositionMapper(e?.platforms?.[platform?.slug]?.disposition),
    location: { ...e.location, breadcrumb: e?.location?.breadcrumb },
    hidePostNowBtn:
      e?.platforms?.[platform?.slug]?.status?.slug === 'active' ||
      e?.platforms?.[platform?.slug]?.status?.slug === 'pending' ||
      e?.platforms?.[platform?.slug]?.disposition?.slug === 'rejected',
    showStats:
      e?.platforms?.[platform?.slug]?.status?.slug === 'active' ||
      e?.platforms?.[platform?.slug]?.disposition?.slug === 'changes-in-review',
    icon: platform?.icon,
    listingPlatformActions: tenantUtils.getListingPlatformActions(e, user, platform?.slug),
    isBadge:
      platform?.slug === 'bayut'
        ? e?.platforms?.bayut?.products_information?.['hot-listing']?.is_applied
        : e?.platforms?.dubizzle?.products_information?.['feature']?.is_applied,
    productsInfo: e?.platforms?.[platform?.slug]?.products_information,
    ...(!!e?.user && { listingOwner: e?.user }),
    user: user,
  };
};

export const listingMapper = (listing, user, setStatsLoading, existingStats = {}) => {
  const platforms = {
    property_id: listing?.id,
    ...(listing?.listing_purpose && {
      purpose: listing?.listing_purpose?.title,
      purposeId: listing?.listing_purpose?.id,
    }),
    expiryDays: listing?.expiry_days || 30,
    isMultiPlatform: user?.isMultiPlatform,
    listingRowActions: (refsObject, loading) => {
      const actions = tenantUtils.getListingRowActions(listing, refsObject, loading, user);
      return actions;
    },
    ...(user?.platforms || []).reduce((acc, platform) => {
      if (platform?.slug) {
        acc[platform.slug] = platformMapper(listing, user, platform, setStatsLoading, existingStats);
      }
      return acc;
    }, {}),
  };
  return {
    ...listing,
    id: listing?.id,
    property: {
      id: listing?.id,
      ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
      type: listing?.listing_type,
      area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.name },
      location: {
        ...listing?.location,
        breadcrumb: listing?.location?.breadcrumb
          ?.filter((e) => e?.level > 1)
          ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
          ?.join(', '),
      },
      image: listing?.images ? listing?.images?.[0] : listing?.image,
      imageCount: listing?.images_count,
      price: { value: listing?.price, currency: listing?.currency },
      details: {
        listingSpecs: tenantData?.getListingSpecs(listing),
        listing_type: listing?.listing_type,
      },
      ...(listing?.health && {
        health: listing?.health,
      }),
      showHealth: true,
    },
    platforms,
  };
};

export const getMyListingsData = (summaryRes, listingsRes, userId) => {
  const listings = listingsRes?.listings;
  const pagination = listingsRes?.pagination;
  const summary = getSummaryData(summaryRes?.summary, listingsRes?.statuses_and_dispositions);
  const loginUser = store.getState().app.loginUser?.user;
  const isAgency = loginUser?.agency?.id;
  let user = {};
  if (isAgency) {
    user = loginUser?.agency?.users?.find((e) => e?.id == userId);
  } else {
    user = loginUser;
  }
  if (!user || userId == -1) {
    user = {
      ...loginUser,
      ...loginUser?.agency,
      name: loginUser?.agency?.name,
      name_l1: loginUser?.agency?.name_l1,
      id: -1,
      profile_image: loginUser?.agency?.agency_logo,
      is_agency_admin: true,
    };
  }
  return {
    list: listings && listings?.length ? listings?.map((item) => listingMapper(item, user)) : [],
    pagination: tenantUtils.getPaginationObject(pagination),
    tabFilterKey: 'q[status_id_eq]',
    dontAllowRowSlection: true,
    statuses_and_dispositions: listingsRes?.statuses_and_dispositions,
    statuses: summary
      ? [
          ...Object.keys(summary).map((e) => ({
            ...summary[e],
            key: e === 'All' ? '' : summary[e].mapping,
            label: `${t(capitalizeFirstLetter(e))} (${summary[e].total})`,
            tab: `${t(capitalizeFirstLetter(e))} (${summary[e].total})`,
          })),
        ]
      : [],
  };
};

const updateListingStats = (listing) => {
  if (listing) {
    return {
      views: listing?.sum_search_count,
      leads: listing?.sum_lead_count,
      clicks: listing?.sum_view_count,
      ctr: (listing?.sum_view_count / listing?.sum_search_count) * 100,
      calls: listing?.sum_phone_view_count,
      emails: listing?.sum_email_lead_count,
      sms: listing?.sum_sms_view_count,
      whatsapp: listing?.sum_whatsapp_view_count,
      chat: listing?.sum_chat_lead_count,
      stats: {
        views: listing?.sum_search_count,
        clicks: listing?.sum_view_count,
        leads: listing?.sum_lead_count,
      },
    };
  } else {
    return {
      views: '',
      leads: '',
      clicks: '',
      ctr: '',
      calls: '',
      emails: '',
      sms: '',
      whatsapp: '',
      chat: '',
      stats: {
        views: '',
        clicks: '',
        leads: '',
      },
      statsError: 'Bayut Stats Error',
    };
  }
};

const getProductDetailToBeApplied = (data, productSlug) => {
  const details = data?.products?.find((it) => it?.slug == productSlug);
  if (details) {
    return {
      ...tenantData.getListingActions(productSlug),
      ...details,
      isSufficient: data?.available_credits >= details?.credits_required?.[30],
      availableCredits: data?.available_credits,
    };
  } else {
    return details;
  }
};

const getProductDetails = (allProducts) => {
  return allProducts?.length
    ? allProducts?.map((e) => {
        const productDetails = tenantData.getListingActions(e?.slug);
        if (productDetails) {
          return { ...productDetails, ...e };
        } else {
          return e;
        }
      })
    : [];
};

const upsellDataMapper = (platformWiseData, listingDetailResponse, platformProductSlugs) => {
  const user = store.getState().app.loginUser.user;

  const products = { bayut: ['hot-listing'], dubizzle: ['feature'] };

  let data = {
    platforms: {},
  };
  user?.platforms?.forEach((e) => {
    if (platformWiseData?.[e?.slug]) {
      if (!listingDetailResponse?.listing?.platforms?.[e?.slug]?.is_posted) return;
      data.platforms[e.slug] = {
        availableCredits: 0,
        creditUnitPrice: 0,
        isPosted: false,
        products: products?.[e?.slug],
      };
    }
  });

  Object.keys(data.platforms)?.forEach((platform) => {
    data.platforms[platform] = {
      ...data?.platforms?.[platform],
      availableCredits: platformWiseData?.[platform]?.available_credits,
      creditUnitPrice: platformWiseData?.[platform]?.credit_unit_price,
      isPosted: listingDetailResponse?.listing?.platforms?.[platform]?.is_posted,
      products: data.platforms?.[platform]?.products
        ?.map((e) => {
          const tenantProduct = tenantData.products.find((pr) => pr?.slug == e);
          const resProduct = platformWiseData?.[platform]?.products?.find((prod) => prod?.slug == e);
          if (!resProduct) return null;
          return {
            ...tenantProduct,
            requiredCredits: resProduct.credits_required,
            price: resProduct.product_price,
            isRecommended: resProduct.is_recommended,
            isAddOn: resProduct.is_add_on,
            activeDuration: listingDetailResponse?.listingExpiryDays || 30,
            id: resProduct.product_id,
            title: tenantUtils.getLocalisedString(resProduct, 'product_title'),
            description: tenantUtils.getLocalisedString(resProduct, 'description'),
          };
        })
        ?.filter(Boolean),
    };
  });
  let totalCredits = 0;
  Object.values(data?.platforms)?.forEach((plat) => {
    if (plat?.products?.length) totalCredits = totalCredits + plat.availableCredits;
  });
  data.currency = totalCredits > 0 ? 'Credits' : tenantConstants.CURRENCY;
  data.headingMsg = 'Listing Posted';
  data.descriptionMsg = `Your listing has been successfully posted`;
  data.listing = listingDetailResponse;
  return data;
};

const listingCardMapper = (listing) => {
  return {
    id: listing?.id,
    ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
    type: listing?.listing_type,
    area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.id || 2 },
    location: {
      ...listing?.location,
      breadcrumb: listing?.location?.breadcrumb
        ?.reverse()
        ?.filter((e) => e?.level > 1)
        ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
        ?.join(', '),
    },
    image: listing?.images?.find((item) => item?.is_main_image) || listing?.images?.[0],
    price: { value: listing?.price, currency: tenantConstants.CURRENCY },
    details: {
      ...listing?.rega_details?.property_specs,
      listingSpecs: tenantData?.getListingSpecs(listing),
      listing_type: listing?.listing_type,
      regaId: listing?.ad_license,
      regaExpiryDate: listing?.platforms?.rega_expiry_date,
    },
    ...(listing?.health && {
      health: listing?.health,
    }),
    disposition: listing?.disposition?.slug,
    permit_number: listing?.permit_number,
  };
};

export const amenitiesDataMapper = (payload) => {
  const fieldType = { number: 'input', text: 'input', checkbox: 'checkbox', select: 'select' };

  const sortIndex = { number: 3, text: 2, checkbox: 4, select: 1 };
  const amenitiesData = {};
  payload &&
    payload.forEach((amenityItem, index) => {
      if (amenityItem.groups.length) {
        Object.defineProperty(amenitiesData, amenityItem.type_id, { value: { fields: {}, fieldsSections: [] } });
      }
      amenityItem.groups.forEach((e, i) => {
        amenitiesData?.[amenityItem.type_id].fieldsSections.push({
          title: tenantUtils.getLocalisedString(e, 'title'),
          fields: [],
        });
        // to sort
        const features = e.features.sort((first, second) => {
          return sortIndex[first.format] < sortIndex[second.format]
            ? -1
            : sortIndex[first.format] == sortIndex[second.format]
              ? 0
              : 1;
        });
        features.forEach((item) => {
          amenitiesData[amenityItem.type_id].fields[item.id] = {
            type: fieldType[item.format],
            initialValue: '',
            value: '',
            props: {
              options:
                fieldType[item.format] === 'select'
                  ? tenantUtils
                      .getLocalisedString(item, 'options')
                      .split(',')
                      .map((e) => ({ id: e, name: e }))
                  : undefined,
              label: tenantUtils.getLocalisedString(item, 'title'),
              horizontal: true,
              id: item.id,
            },
          };
          amenitiesData[amenityItem.type_id].fieldsSections[i].fields.push(item.id);
        });
      });
    });
  return { data: amenitiesData };
};

const productsDataMapper = (response) => {
  let productsByPlatform = {};
  const productList = response?.products;

  tenantData.platformList.forEach(({ key, responseKey }) => {
    productsByPlatform[key] = [];
    const platformProducts = productList?.filter((e) => e?.platform?.slug == responseKey);
    platformProducts?.forEach((product) => {
      const { product_id, slug } = product;

      productsByPlatform[key].push({
        ...product,
        ...tenantData.getListingActions(slug),
        product_id,
        price: product?.product_price,
      });
    });
  });

  return {
    platformProducts: productsByPlatform,
    products: !!productList?.length ? productList.map((e) => ({ ...tenantData.getListingActions(e?.slug), ...e })) : [],
  };
};

export const dataMapper = (platformWiseData, listingDetailResponse) => {
  const productsHash = {};
  tenantData.products.forEach((e) => {
    productsHash[e.slug] = e;
  });

  const data = {
    availableCredits: platformWiseData?.available_credits,
    creditUnitPrice: platformWiseData?.credit_unit_price,
    isPosted: listingDetailResponse?.isListingPosted,
    // isPosted: true,
    headingMsg: '',
    descriptionMsg: '',
    currency: '',
    applicableProducts: [],
    subServices: [],
    serviceStates: {},
    autoRenewState: {},
    listing: listingDetailResponse?.listing,
  };

  data?.availableCredits > 0 ? (data.currency = 'Credits') : (data.currency = t(tenantConstants.CURRENCY));

  platformWiseData?.products.forEach((item) => {
    if (item.is_add_on === false && item.slug !== 'refresh') {
      data.applicableProducts.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        isAddOn: item.is_add_on,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.autoRenewState[item.product_id] = false;
    } else if (item.is_add_on === true) {
      data.subServices.push({
        ...productsHash[item.slug],
        requiredCredits: item.credits_required,
        price: item.product_price,
        isRecommended: item.is_recommended,
        activeDuration: listingDetailResponse?.listingExpiryDays || 30,
        id: item.product_id,
        title: tenantUtils.getLocalisedString(item, 'product_title'),
        description: tenantUtils.getLocalisedString(item, 'description'),
      });
      data.serviceStates[item.product_id] = false;
    }
  });
  data.headingMsg = listingDetailResponse?.isListingPosted
    ? 'Your Listing has been Posted Successfully!'
    : 'Almost There';
  data.descriptionMsg = listingDetailResponse?.isListingPosted
    ? 'Make your Listing Hot and reach 10x more Buyers'
    : 'Choose the type of listing you want to publish';

  return data;
};

export const mapPlatformData = (res, platforms, listingDetailResponse) => {
  const data = {};
  platforms?.forEach((platform) => {
    data[platform?.slug] = dataMapper(res, listingDetailResponse);
  });
  return data;
};

const widgetParser = (listings, platform) => {
  const productsToShow = {
    bayut: [{ slug: 'hot-listing' }, { slug: 'basic-listing' }],
    dubizzle: [{ slug: 'basic-listing-dubizzle' }, { slug: 'feature' }, { slug: 'boost-to-top' }],
  };
  const data = {
    [platform]: {
      title: 'Listings',
      icon_data: {},
      link_data: {
        text: 'View All Listings',
        to: '/listings',
      },
      total_title: 'Active',
      total_title_icon: 'MdCircle',
      total_value: '',
      purposes: [
        {
          id: 1,
          icon: 'IconForSale',
          iconProps: { color: tenantTheme['color-for-sale'], hasBackground: true },
          title: 'For Sale',
          value: '',
        },
        {
          id: 2,
          icon: 'IconForRent',
          iconProps: { color: tenantTheme['color-for-rent'], hasBackground: true },
          title: 'To Rent',
          value: '',
        },
        // {
        //   id: 4,
        //   icon: 'IconRental',
        //   iconProps: { color: tenantTheme['color-for-rent'], hasBackground: true },
        //   title: 'Daily Rentals',
        //   value: '',
        // },
      ],
      products: tenantUtils.generateProductData(productsToShow?.[platform]),
    },
  };

  const currentPlatform = data?.[platform];
  currentPlatform.total_value = listings?.active;
  currentPlatform.purposes[0].value = listings?.sale;
  currentPlatform.purposes[1].value = listings?.rent;
  // currentPlatform.purposes[2].value = listings?.daily_rental;
  currentPlatform.products.forEach((item, i) => {
    currentPlatform.products[i].value = listings?.[item.slug_alt];
  });

  return data?.[platform];
};

const getListingSummaryStats = (response, platform) => {
  return { [platform]: widgetParser(response?.data?.stats?.platforms?.[platform], platform) };
};

export const listingDetailMapper = (listing, platforms) => {
  return {
    id: listing?.id,
    permit_number: listing?.permit_number,
    ...(listing?.listing_purpose && { purpose: listing?.listing_purpose }),
    isDailyRental: listing?.listing_purpose?.slug == 'daily-rental',
    isOffPlan: listing?.is_offplan_listing || null,
    projectStatus: listing?.completion_status === 'ready' ? 'Ready' : 'Off-Plan',
    saleType: listing?.sale_type,
    floorPlan: listing?.unit_variant?.floor_plan.length > 0 ? true : false,
    completion_status: listing?.completion_status,
    type: listing?.listing_type,
    area: { value: listing?.area_unit?.value, unit: listing?.area_unit?.name },
    location: {
      ...listing?.location,
      breadcrumb: listing?.location?.breadcrumb
        ?.reverse()
        ?.filter((e) => e?.level > 1)
        ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
        ?.join(', '),
    },
    image: listing?.image,
    imageCount: listing?.images_count,
    price: { value: listing?.price, currency: tenantConstants.CURRENCY },
    details: {
      ...listing?.rega_details?.property_specs,
      listingSpecs: tenantData?.getListingSpecs(listing),
      listing_type: listing?.listing_type,
      regaId: listing?.ad_license,
      regaExpiryDate: listing?.platforms?.rega_expiry_date,
    },
    ...(listing?.health && {
      health: listing?.health,
    }),
    showHealth: true,
    productsInfo: listing?.platforms?.ksa?.products_information,
    isBadge: listing?.platforms?.ksa?.products_information?.['basic-listing']?.is_applied,
    status: listing?.status?.slug,
    platforms: platforms,
    url: tenantUtils.getLocalisedString(listing, 'url'),
  };
};

export default {
  getMyListingsData,
  productsDataMapper,
  mapPlatformData,
  listingMapper,
  platformMapper,
  amenitiesDataMapper,
  updateListingStats,
  getProductDetailToBeApplied,
  getProductDetails,
  postedToBayut,
  upsellDataMapper,
  getListingSummaryStats,
  listingCardMapper,
  listingDetailMapper,
};
